import Button from '@components/Button'
import DropdownMenu, { DropdownSection } from '@components/DropdownMenu'
import { RainbowAvatar } from '@components/RainbowAvatar/RainbowAvatar'
import { logger } from '@plug-core/logger'
import { useCurrentWallet, useWalletProperties } from '@plug-core/state'
import { Principal } from '@dfinity/principal'
import { getPrincipals } from '@plug-core/handlers/walletActions/wallet'
import { shortAddress } from '@plug-core/utils/shortAddress'
import WalletDetailsPrompt from '@modules/Wallet/Components/WalletDetailsPrompt'
import {
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  Stack,
  useTheme,
} from '@mui/material'
import { ROUTES } from '@utils/routes'
import React, { useCallback, useEffect, useState } from 'react'
import { HiOutlineDotsCircleHorizontal, HiOutlineUserCircle, HiCheckCircle } from 'react-icons/hi'
import { useNavigate } from 'react-router-dom'

const AccountList = () => {
  const [principals, setPrincipals] = useState<Principal[]>([])
  const { currentWallet, setCurrentWallet } = useCurrentWallet()
  const { walletNames, walletEmojis, setWalletName, setWalletEmoji } = useWalletProperties()
  const navigate = useNavigate()

  const [promptVisible, setPromptVisible] = useState(false)
  const [name, setName] = useState<string>()
  const [emoji, setEmoji] = useState<string>('🚀')

  const [walletInEdit, setWalletInEdit] = useState<string>()

  const { palette } = useTheme()

  useEffect(() => {
    getPrincipals()
      .then(setPrincipals)
      .catch((e) => logger.error('Failed to get principals', { originalError: e }))
  }, [])

  const editWallet = useCallback(
    (wallet: string) => {
      setPromptVisible(true)
      setWalletInEdit(wallet)
      setName(walletNames[wallet])
      setEmoji(walletEmojis[wallet] || '🚀')
    },
    [walletNames]
  )

  const updateWallet = useCallback(() => {
    setWalletName(walletInEdit!, name!)
    setWalletEmoji(walletInEdit!, emoji!)
    setPromptVisible(false)
  }, [walletInEdit, name, emoji])

  const getDropdownSections = useCallback(
    (walletId: string): DropdownSection[] => [
      {
        items: [
          {
            name: 'Edit Wallet',
            icon: HiOutlineUserCircle,
            action: () => editWallet(walletId),
          },
        ],
      },
    ],
    [editWallet]
  )

  const selectWallet = (principal: string) => {
    setCurrentWallet(principal.toString())
    navigate(-1)
  }

  return (
    <>
      <WalletDetailsPrompt
        show={promptVisible}
        name={name}
        emoji={emoji}
        setName={setName}
        setEmoji={setEmoji}
        onSave={updateWallet}
        onClose={() => setPromptVisible(false)}
        isEdit
      />
      <Stack spacing={2} px={2}>
        <Stack component={List}>
          {principals.map((principal) => (
            <ListItem onClick={() => selectWallet(principal.toString())} key={principal.toString()}>
              <Stack>
                <RainbowAvatar avatar={walletEmojis[principal.toString()] || '🚀'} size={35} />
                {currentWallet === principal.toString() && (
                  <Stack
                    position="absolute"
                    bottom={10}
                    bgcolor={(theme) => theme.palette.background.default}
                    padding={0.2}
                    borderRadius={2}
                  >
                    <HiCheckCircle size={20} color={palette.rainbow.green} />
                  </Stack>
                )}
              </Stack>

              <ListItemText
                primary={walletNames[principal.toString()] || 'account1'}
                secondary={shortAddress(principal.toString())}
                sx={{ marginLeft: 2 }}
              />
              <ListItemSecondaryAction>
                <DropdownMenu
                  trigger={
                    <Stack>
                      <HiOutlineDotsCircleHorizontal size={25} />
                    </Stack>
                  }
                  sections={getDropdownSections(principal.toString())}
                />
              </ListItemSecondaryAction>
            </ListItem>
          ))}
        </Stack>
        <Button onClick={() => navigate(ROUTES.WALLET_IMPORT_TYPE)} variant="rainbow" fullWidth>
          Add another wallet
        </Button>
      </Stack>
    </>
  )
}

export default AccountList
